import {
    Autocomplete,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import React from "react";
import { useIntl } from "react-intl";

import { OPTIONS_COUNTRIES, localizeCountryOption } from "../../../lib/i18n/countryOptions";
import { initialValue } from "../../Form/helpers";
import { QuestionProps } from "../interfaces";

/** Input component for questions where the responder can select several options as their answer. */
const MultiSelectQuestion: React.FC<QuestionProps> = ({ question, saveDraft, name, ...props }) => {
    const { values, getFieldMeta, errors, setFieldValue } = useFormikContext<Record<string, initialValue>>();
    const { formatMessage, locale } = useIntl();

    const optionLabel = (option: string) =>
        question.optionsTranslationKey === OPTIONS_COUNTRIES ? localizeCountryOption(option, locale) : option;

    const formValue = values[question.id];
    const error = errors[question.id];
    const { touched } = getFieldMeta(`${question.id}.value`);

    if (!question.options || question.options.length < 1 || !formValue) {
        return null;
    }

    const { value } = formValue;

    const showError = touched && Boolean(error);
    // Ensure `value` is always an array for MultiSelect Autocomplete and existing Checkbox logic
    const currentSelection = Array.isArray(value) ? value : [];

    const handleChange = (option: string) => {
        const newList = currentSelection.includes(option)
            ? currentSelection.filter((v) => v !== option)
            : [...currentSelection, option];
        setFieldValue(question.id, { value: newList, fileNames: formValue.fileNames });
        saveDraft(newList);
    };

    const handleAutocompleteChange = (_: React.SyntheticEvent, newValues: string[]) => {
        setFieldValue(question.id, { value: newValues, fileNames: formValue.fileNames });
        saveDraft(newValues);
    };

    return (
        <FormControl sx={{ width: "100%" }} error={showError}>
            {question.options.length > 5 ? (
                <Autocomplete
                    multiple
                    options={question.options}
                    value={currentSelection}
                    onChange={handleAutocompleteChange}
                    disableCloseOnSelect
                    getOptionLabel={optionLabel}
                    disabled={props.disabled}
                    renderOption={(props, option) => (
                        <Typography variant="body1" {...props}>
                            {optionLabel(option)}
                        </Typography>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={formatMessage({
                                defaultMessage: "Select one or more",
                            })}
                        />
                    )}
                />
            ) : (
                <FormGroup {...props} sx={{ gap: 1 }}>
                    {question.options.map((option) => (
                        <FormControlLabel
                            key={option}
                            checked={currentSelection.includes(option)}
                            disabled={props.disabled}
                            control={
                                <Checkbox
                                    checked={currentSelection.includes(option)}
                                    name={option}
                                    onChange={() => handleChange(option)}
                                />
                            }
                            label={optionLabel(option)}
                        />
                    ))}
                </FormGroup>
            )}
            <FormHelperText>
                <Stack direction="row" justifyContent="flex-start">
                    <Typography>{showError && error ? error.value : ""}</Typography>
                </Stack>
            </FormHelperText>
        </FormControl>
    );
};

export default MultiSelectQuestion;
